import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { FormButton } from "../../styles/button";
import {
  ApiError,
  FormGroup,
  InputContainer,
  InputRow,
} from "../../styles/form";
import { SpaceBetweenButtons } from "../../styles/spaces";
import Page from "../layout/Page";
import { handleLoadWorkflowSuggestions } from "../../utils/suggestions";
import Suggestion, { ISuggestionValue } from "../common/suggestion/Suggestion";
import workflowApi from "../../api/workflow";
import {
  IWorkflowQuestion,
  WorkflowSelectedQuestions,
} from "../../models/workflow";
import { errorSet } from "../../utils/error";
import { InputLabel } from "../common/form/InputStyles";
import { TailSpin } from "react-loader-spinner";
import { COLORS } from "../../styles/colors";
import {
  getSelectedAnswers,
  isAllQuestionsSelected,
  renderQuestions,
} from "../demand/DemandQuestions";
import demandApi from "../../api/demand";
import { promiseToastSave } from "../../utils/toasts";
import { clientDemandListLoad } from "../../store/clientDemandList";

interface IProps {
  clientDemandListLoad(reload: boolean): void;
}

const ClientDemandNew: FC<IProps> = ({ clientDemandListLoad }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clientId: paramClientId } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<
    ISuggestionValue | undefined
  >();
  const [questions, setQuestions] = useState<IWorkflowQuestion[]>([]);
  const [selectedQuestions, setSelectedQuestions] =
    useState<WorkflowSelectedQuestions>({});

  const canSave =
    !loading &&
    !saving &&
    !!selectedWorkflow &&
    isAllQuestionsSelected(questions, selectedQuestions, null);

  const handleSelectWorkflow = async (value: ISuggestionValue | undefined) => {
    setLoading(true);
    setError(null);
    setSelectedWorkflow(undefined);
    setQuestions([]);

    if (value) {
      try {
        const res = await workflowApi.getQuestions(
          value.id,
          parseInt(paramClientId!)
        );
        setQuestions(res.data);
        setSelectedWorkflow(value);
      } catch (err) {
        errorSet(setError, err, t);
      }
    }

    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await promiseToastSave(async () => {
        const answers: number[] = [];
        getSelectedAnswers(answers, questions, selectedQuestions, null);

        await demandApi.createDemand(
          selectedWorkflow!.id,
          parseInt(paramClientId!),
          answers
        );

        clientDemandListLoad(true);
        navigate(`/client/${paramClientId}/demand`);
      });
    } catch (err) {
      errorSet(setError, err, t);
    }

    setSaving(false);
  };

  const handleCancel = () => {
    navigate(`/client/${paramClientId}/demand`);
  };

  return (
    <Page>
      <h1>{t("demand.detailTitleNew")}</h1>
      <FormGroup>
        <InputRow>
          <InputLabel htmlFor={"workflowId"}>{t("demand.workflow")}</InputLabel>
          <InputContainer>
            <Suggestion
              id="workflowId"
              selected={selectedWorkflow}
              disabledBySelected={!!selectedWorkflow}
              placeholder={t("demand.workflowPlaceholder")}
              loadSuggestions={handleLoadWorkflowSuggestions}
              selectValue={handleSelectWorkflow}
            />
          </InputContainer>
        </InputRow>
        {renderQuestions(
          questions,
          selectedQuestions,
          setSelectedQuestions,
          null
        )}
        {loading && (
          <TailSpin color={COLORS.loaderColor} width={48} height={48} />
        )}
      </FormGroup>
      {error && <ApiError>{error}</ApiError>}
      <FormButton
        type="button"
        ver="secondary"
        disabled={loading}
        onClick={handleCancel}
      >
        {t("common.back")}
      </FormButton>
      <SpaceBetweenButtons />
      <FormButton type="button" disabled={!canSave} onClick={handleSave}>
        {t("common.save")}
      </FormButton>
    </Page>
  );
};

const mapDispachToProps = {
  clientDemandListLoad,
};

export default connect(null, mapDispachToProps)(ClientDemandNew);
